import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import SearchableSelect from '@/components/common/SearchableSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fetchReportOptions, reportAdvisor } from '@/data/advisor-chat';

interface Props {
  advisorProfileId: string;
  advisorName: string;
  onClose: () => void;
}


export default function ReportAdvisorDialog({
  advisorProfileId,
  advisorName,
  onClose,
}: Props) {
  const [category, setCategory] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [problem, setProblem] = React.useState('');

  const options = useQuery({
    queryKey: ['advisor-report-options'],
    queryFn: fetchReportOptions,
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () =>
      reportAdvisor({
        advisor_profile_id: advisorProfileId,
        category,
        details,
      }),
    onSuccess: () => {
      setProblem('');
      setSent(true);
      toast.success('Reported to BitSwan');
    },
    onError: (error) => {
      const message = apiError(error, 'Could not send that report.');
      setProblem(message);
      toast.error(message);
    },
  });

  const ready = Boolean(category) && details.trim().length >= 20 && !submit.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="report-advisor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            Report {advisorName}
          </DialogTitle>
          <DialogDescription>
            This goes to BitSwan, not to them. They are not told that you
            reported them, and you do not have to stop working with them to
            do it.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3 py-2" data-testid="report-advisor-sent">
            <p className="text-sm">
              Thank you. Someone at BitSwan will look at this.
            </p>
            <p className="text-sm text-muted-foreground">
              If you are in immediate difficulty, email
              support@bitswan.space as well.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {problem && (
              <p
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                data-testid="report-advisor-error"
              >
                {problem}
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="report-category">What happened?</Label>
              <SearchableSelect
                value={category}
                onChange={setCategory}
                placeholder="Choose…"
                searchPlaceholder="Search…"
                options={(options.data?.categories ?? []).map((option) => ({ value: option.value, label: option.label }))}
                testId="report-category"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="report-details">Tell us about it</Label>
              <Textarea
                id="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={5}
                placeholder="What was said or done, and when."
                data-testid="report-details"
              />
              <p className="text-xs text-muted-foreground">
                Someone will read this and decide what to do, so please be specific.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {sent ? (
            <Button onClick={onClose} data-testid="report-advisor-done">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => submit.mutate()}
                disabled={!ready}
                data-testid="report-advisor-submit"
              >
                {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send report
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
